import { Token } from '@frontend/authentication-v2';
import { useAppSelector } from '@frontend/common';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AuthorizationClient } from '../../api/authorization-client';
import { UserPermissions } from '../../permission';
import { authorizationStore } from '../../redux/authorization-slice';

const STALE_MS = 10 * 60 * 1000;

const AuthorizationContext = createContext({
    permissions: {} as UserPermissions | null,
    getPermissions: (): void => {},
    changeLastRequest: (date: number): void => {}
});

export const AuthorizationProvider = (props: { children: any; dispatch: ThunkDispatch<any, any, Action>; token?: Token }) => {
    const authorizationState = useAppSelector(useSelector, authorizationStore);
    const lastUpdateRef = useRef<number>(Date.now());
    const lastRequestRef = useRef<number>(Date.now());
    const [permissions, changePermissions] = useState<UserPermissions | null>(null);

    const getPermissions = useCallback((): void => {
        AuthorizationClient.fetchPermissions(props.token).then((result) => {
            changePermissions(result);
        });
    }, [props.token]);

    // Activity marker only — must not setState or every AuthorizationWrapper check re-renders the tree.
    const changeLastRequest = useCallback(
        (date: number) => {
            lastRequestRef.current = date;
            if (lastUpdateRef.current < date - STALE_MS) {
                lastUpdateRef.current = Date.now();
                getPermissions();
            }
        },
        [getPermissions]
    );

    useEffect(() => {
        getPermissions();
        lastUpdateRef.current = Date.now();
        lastRequestRef.current = Date.now();
    }, [getPermissions]);

    useEffect(() => {
        if (authorizationState.permissions) {
            changePermissions(authorizationState.permissions);
        }
    }, [authorizationState.permissions]);

    const value = useMemo(() => ({ permissions, getPermissions, changeLastRequest }), [permissions, getPermissions, changeLastRequest]);

    return <AuthorizationContext.Provider value={value}>{props.children}</AuthorizationContext.Provider>;
};

export const useAuthorization = () => {
    const context = useContext(AuthorizationContext);
    if (!context) {
        throw new Error('useAuthorization must be used within an AuthorizationProvider');
    }
    return context;
};
